/**
 * useClientSearch
 * Hook for debounced client search in facturation
 * Mirrors the strategy from search tab's useCustomerSearch
 */

import { useState, useCallback, useEffect } from 'react';
import { apiClient } from '@/core/api/client';

export interface FacturationClient {
  id: string;
  fmClientId: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  company: string | null;
  hasAccount: boolean;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export const useClientSearch = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [clients, setClients] = useState<FacturationClient[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    const searchClients = async () => {
      // Only clear results when query is completely empty
      if (!searchQuery.trim()) {
        setClients([]);
        setHasSearched(false);
        setLoading(false);
        return;
      }

      // Don't search if query is too short, but keep existing results visible
      if (searchQuery.trim().length < 2) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams();
        params.append('searchQuery', searchQuery.trim());

        const response = await apiClient.get(
          `/api/admin/customers/search?${params}`,
          { signal: controller.signal }
        ) as ApiResponse<{ customers: FacturationClient[] }>;

        if (!controller.signal.aborted) {
          if (response.success) {
            // Only update results when new data arrives
            setClients(response.data.customers || []);
            setHasSearched(true);
          } else {
            setError(response.message || 'Echec de la recherche de clients');
            // Keep existing results on error instead of clearing
          }
        }
      } catch (err: unknown) {
        const error = err as { name?: string };
        if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
          console.error('Error searching clients:', err);
          if (!controller.signal.aborted) {
            setError('Echec de la recherche de clients');
            // Keep existing results on error instead of clearing
          }
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    // Debounce search - 300ms delay
    const timeoutId = setTimeout(searchClients, 300);
    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [searchQuery]);

  const search = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const reset = useCallback(() => {
    setSearchQuery('');
    setClients([]);
    setHasSearched(false);
    setError(null);
  }, []);

  return {
    clients,
    loading,
    error,
    search,
    searchQuery,
    setSearchQuery,
    hasSearched,
    reset,
  };
};

export default useClientSearch;
